import { FXPServiceCatalog, FxpServiceFactory } from '@fxp/fxpservices-di';

/**
 * FXP navigation for cross-team destinations.
 *
 * The FM Dashboard links to screens owned by OTHER partner modules (`skillsearch`,
 * `resourcedashboard`, OneProfile). Legacy never hardcoded their URLs — it resolved them through
 * the FXP router BY STATE NAME (`fmdashboard.controller.js:42` for Resource Search;
 * `$state.href('grm-resourcedashboardfm', …)` + open-in-new-tab at `:206-211`), so those teams stay
 * free to change their own URL shape.
 *
 * Every helper degrades safely: when the FXP shell is absent (Jest or local component testing
 * without the shell) the caller's `fallbackUrl` is used, so the local loop still renders.
 */

interface FxpRouteServiceLike {
  navigatetoSpecificState?: (stateName: string, params?: Record<string, string>) => void;
  navigateToSpecificUrl?: (url: string, target?: string) => void;
}

interface FxpStateServiceLike {
  href?: (stateName: string, params?: Record<string, string>) => string | undefined;
}

function getRouteService(): FxpRouteServiceLike | undefined {
  try {
    return FxpServiceFactory.getServiceInstance(
      FXPServiceCatalog.FxpRouteService
    ) as FxpRouteServiceLike;
  } catch {
    return undefined;
  }
}

function getStateService(): FxpStateServiceLike | undefined {
  try {
    return FxpServiceFactory.getServiceInstance(
      FXPServiceCatalog.StateService
    ) as FxpStateServiceLike;
  } catch {
    return undefined;
  }
}

/**
 * Resolve a registered FXP state to a URL. Returns `undefined` when the shell isn't present or
 * the state isn't registered — callers must then fall back rather than guess a URL.
 */
export function stateHref(
  stateName: string,
  params?: Record<string, string>
): string | undefined {
  try {
    const href = getStateService()?.href?.(stateName, params);
    return href ? String(href).replace(/\s/g, '') : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Navigate the shell to another partner app's state, by name. Returns false when the FXP router
 * is unavailable so the caller can fall back to a plain anchor.
 */
export function navigateToState(
  stateName: string,
  params?: Record<string, string>
): boolean {
  const route = getRouteService();
  if (!route?.navigatetoSpecificState) {
    return false;
  }
  try {
    route.navigatetoSpecificState(stateName, params ?? {});
    return true;
  } catch {
    return false;
  }
}

/** Open a URL in a new tab through FXP, falling back to `window.open`. */
export function openUrlInNewTab(url: string): void {
  if (!url) {
    return;
  }
  const route = getRouteService();
  if (route?.navigateToSpecificUrl) {
    try {
      route.navigateToSpecificUrl(url, '_blank');
      return;
    } catch {
      // fall through to window.open
    }
  }
  if (typeof window !== 'undefined' && window.open) {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

/**
 * Resolve a state to a URL and open it in a new tab, preferring the FXP-registered URL and
 * using `fallbackUrl` only when the shell can't resolve the state.
 */
export function openStateInNewTab(
  stateName: string,
  params: Record<string, string>,
  fallbackUrl?: string
): void {
  const url = stateHref(stateName, params) ?? fallbackUrl;
  if (url) {
    openUrlInNewTab(url);
  }
}
